import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { PublicNav } from "@/components/PublicNav";
import { PublicFooter } from "@/components/PublicFooter";
import { moneyInputProps, sanitizeMoney, parseMoneyInput } from "@/lib/money-input";

export const Route = createFileRoute("/tools/real-wage-growth-calculator")({
  head: () => ({
    meta: [
      { title: "Reallønnsvekst kalkulator — juster lønn for KPI | VISIKT" },
      {
        name: "description",
        content:
          "Gratis reallønnsvekst-kalkulator. Sammenlign gammel og ny lønn justert for norsk KPI (konsumprisindeks) og se om kjøpekraften din faktisk har økt.",
      },
      { property: "og:title", content: "Reallønnsvekst kalkulator — VISIKT" },
      {
        property: "og:description",
        content:
          "Regn ut reallønnsvekst basert på norsk KPI. Se om lønnsøkningen din slår inflasjonen.",
      },
      {
        property: "og:url",
        content: "https://visikt.com/tools/real-wage-growth-calculator",
      },
      { property: "og:type", content: "website" },
    ],
    links: [
      {
        rel: "canonical",
        href: "https://visikt.com/tools/real-wage-growth-calculator",
      },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebApplication",
          name: "Reallønnsvekst kalkulator",
          applicationCategory: "FinanceApplication",
          operatingSystem: "Web",
          url: "https://visikt.com/tools/real-wage-growth-calculator",
          description:
            "Kalkulator som justerer lønnsvekst for norsk konsumprisindeks (KPI) og viser reell endring i kjøpekraft.",
          offers: { "@type": "Offer", price: "0", priceCurrency: "NOK" },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: [
            {
              "@type": "Question",
              name: "Hva er reallønnsvekst?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Reallønnsvekst er lønnsveksten din justert for prisstigning (KPI). Hvis lønnen øker 4 % og KPI øker 3 %, er reallønnsveksten omtrent 1 %.",
              },
            },
            {
              "@type": "Question",
              name: "Hvordan regnes reallønnsvekst ut?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Reallønn = nominell lønn delt på KPI-faktor. Reallønnsvekst i prosent = ((1 + lønnsvekst) / (1 + inflasjon) − 1) × 100.",
              },
            },
            {
              "@type": "Question",
              name: "Hvor kommer KPI-tallene fra?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "KPI-tallene er hentet fra Statistisk sentralbyrå (SSB) sin serie for konsumprisindeksen i Norge.",
              },
            },
          ],
        }),
      },
    ],
  }),
  component: RealWageGrowthCalculator,
});

// Norwegian CPI (KPI) — annual average, 2015 = 100. Source: SSB (Statistisk sentralbyrå).
// Update when new annual figures are published.
const KPI: Record<number, number> = {
  2015: 100.0,
  2016: 103.6,
  2017: 105.5,
  2018: 108.4,
  2019: 110.8,
  2020: 112.2,
  2021: 116.1,
  2022: 122.8,
  2023: 129.4,
  2024: 133.5,
  2025: 137.6,
};

const YEARS = Object.keys(KPI)
  .map(Number)
  .sort((a, b) => a - b);

function formatNok(n: number): string {
  return new Intl.NumberFormat("nb-NO", {
    style: "decimal",
    maximumFractionDigits: 0,
  }).format(Math.round(n));
}

function formatPct(n: number, digits = 1): string {
  const sign = n > 0 ? "+" : "";
  return `${sign}${n.toFixed(digits)} %`;
}

function RealWageGrowthCalculator() {
  const [fromYear, setFromYear] = useState(2020);
  const [toYear, setToYear] = useState(YEARS[YEARS.length - 1]);
  // Keep the raw string in state so user input like "650 000" or "650000,50"
  // is not silently reset by native `type="number"` parsing.
  const [oldSalaryStr, setOldSalaryStr] = useState("600000");
  const [newSalaryStr, setNewSalaryStr] = useState("680000");

  const oldSalary = parseMoneyInput(oldSalaryStr);
  const newSalary = parseMoneyInput(newSalaryStr);

  const result = useMemo(() => {
    const kpiFrom = KPI[fromYear];
    const kpiTo = KPI[toYear];
    if (
      !kpiFrom ||
      !kpiTo ||
      oldSalary == null ||
      newSalary == null ||
      Number.isNaN(oldSalary) ||
      Number.isNaN(newSalary) ||
      oldSalary <= 0
    )
      return null;
    const inflationFactor = kpiTo / kpiFrom;
    const inflationPct = (inflationFactor - 1) * 100;
    const nominalPct = (newSalary / oldSalary - 1) * 100;
    const equivalentSalary = oldSalary * inflationFactor; // what old salary would be today
    const realSalary = newSalary / inflationFactor; // new salary in old-year kroner
    const realGrowthPct = (newSalary / oldSalary / inflationFactor - 1) * 100;
    return {
      inflationPct,
      nominalPct,
      realGrowthPct,
      equivalentSalary,
      realSalary,
      purchasingPowerDelta: newSalary - equivalentSalary,
    };
  }, [fromYear, toYear, oldSalary, newSalary]);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="max-w-2xl mx-auto px-5 pt-8 flex items-center justify-between">
        <span className="font-display font-extrabold tracking-tighter text-xl">VISIKT</span>
        <PublicNav />
      </header>

      <main className="max-w-2xl mx-auto px-5 pt-16 pb-20 space-y-16">
        <section className="animate-entry">
          <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-4">
            Gratis verktøy · Norge
          </p>
          <h1 className="text-4xl font-display font-bold tracking-tight leading-[1.05] text-balance">
            Reallønnsvekst kalkulator
          </h1>
          <p className="mt-4 text-base text-muted-foreground leading-relaxed max-w-prose">
            Har lønnsøkningen din faktisk gitt deg mer å rutte med? Sammenlign gammel og ny lønn
            justert for norsk konsumprisindeks (KPI) fra Statistisk sentralbyrå, og se den reelle
            endringen i kjøpekraft.
          </p>
        </section>

        <section className="animate-entry [animation-delay:60ms] border border-border rounded-2xl p-6 space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <label className="block">
              <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                Fra år
              </span>
              <select
                value={fromYear}
                onChange={(e) => setFromYear(Number(e.target.value))}
                className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
              >
                {YEARS.map((y) => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
              </select>
            </label>
            <label className="block">
              <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                Til år
              </span>
              <select
                value={toYear}
                onChange={(e) => setToYear(Number(e.target.value))}
                className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
              >
                {YEARS.map((y) => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
              </select>
            </label>
            <label className="block">
              <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                Lønn i {fromYear} (NOK)
              </span>
              <input
                {...moneyInputProps}
                value={oldSalaryStr}
                onChange={(e) => setOldSalaryStr(sanitizeMoney(e.target.value))}
                className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2 text-sm tabular-nums"
              />
            </label>
            <label className="block">
              <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                Lønn i {toYear} (NOK)
              </span>
              <input
                {...moneyInputProps}
                value={newSalaryStr}
                onChange={(e) => setNewSalaryStr(sanitizeMoney(e.target.value))}
                className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2 text-sm tabular-nums"
              />
            </label>
          </div>

          {result && fromYear !== toYear && (
            <div className="border-t border-border pt-6 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <Stat label="Nominell lønnsvekst" value={formatPct(result.nominalPct)} />
                <Stat label="KPI-inflasjon" value={formatPct(result.inflationPct)} />
                <Stat
                  label="Reallønnsvekst"
                  value={formatPct(result.realGrowthPct)}
                  emphasized
                  positive={result.realGrowthPct >= 0}
                />
              </div>
              <p className="text-sm text-muted-foreground leading-relaxed">
                For å beholde samme kjøpekraft som i {fromYear} måtte lønnen i {toYear} vært{" "}
                <strong className="text-foreground tabular-nums">
                  {formatNok(result.equivalentSalary)} NOK
                </strong>
                . Faktisk lønn er{" "}
                <strong className="text-foreground tabular-nums">
                  {formatNok(newSalary ?? 0)} NOK
                </strong>{" "}
                — en reell endring på{" "}
                <strong
                  className={
                    result.purchasingPowerDelta >= 0 ? "text-foreground" : "text-destructive"
                  }
                >
                  {result.purchasingPowerDelta >= 0 ? "+" : "−"}
                  {formatNok(Math.abs(result.purchasingPowerDelta))} NOK
                </strong>{" "}
                per år i {fromYear}-kroner.
              </p>
            </div>
          )}
          {fromYear === toYear && (
            <p className="text-sm text-muted-foreground">Velg to forskjellige år for å regne ut.</p>
          )}
        </section>

        <section className="animate-entry [animation-delay:120ms] border border-border rounded-2xl p-6 bg-foreground text-background">
          <p className="font-mono text-[10px] uppercase tracking-widest opacity-70 mb-2">
            Følg utviklingen over tid
          </p>
          <h2 className="text-2xl font-display font-bold tracking-tight">
            Slipp regnearket. La VISIKT holde styr på reallønnen din.
          </h2>
          <p className="mt-3 text-sm opacity-80 leading-relaxed max-w-prose">
            Legg inn lønnen hver gang den endres, så viser VISIKT reallønnsveksten din år for år —
            sammen med faste utgifter og kjøpekraft. Gratis å komme i gang.
          </p>
          <Link
            to="/auth"
            className="mt-5 inline-flex items-center justify-center rounded-md bg-background text-foreground font-semibold text-sm px-5 py-3 hover:bg-background/90 transition-colors"
          >
            Opprett gratis konto
          </Link>
        </section>

        <section className="animate-entry [animation-delay:180ms] space-y-6">
          <h2 className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
            Slik fungerer det
          </h2>
          <div className="space-y-4 text-sm leading-relaxed">
            <p>
              <strong>Reallønnsvekst</strong> er lønnsveksten din justert for prisstigning. Hvis
              lønnen din øker 5 % mens prisene stiger 4 %, sitter du i praksis igjen med rundt 1 %
              mer å bruke.
            </p>
            <p>
              Kalkulatoren bruker <strong>konsumprisindeksen (KPI)</strong> fra Statistisk
              sentralbyrå som mål på inflasjon i Norge. Formelen er:
            </p>
            <pre className="font-mono text-xs bg-muted rounded-md p-4 overflow-x-auto">
              {`reallønnsvekst = ((1 + nominell lønnsvekst) / (1 + inflasjon)) − 1`}
            </pre>
            <p className="text-muted-foreground">
              KPI-tallene i denne kalkulatoren er årsgjennomsnitt med 2015 = 100. Kilde: SSB, tabell
              for konsumprisindeksen.
            </p>
          </div>
        </section>

        <PublicFooter />
      </main>
    </div>
  );
}

function Stat({
  label,
  value,
  emphasized,
  positive,
}: {
  label: string;
  value: string;
  emphasized?: boolean;
  positive?: boolean;
}) {
  return (
    <div>
      <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
        {label}
      </p>
      <p
        className={
          "mt-1 font-display font-bold tabular-nums " +
          (emphasized ? "text-3xl " : "text-xl ") +
          (emphasized && positive === false ? "text-destructive" : "text-foreground")
        }
      >
        {value}
      </p>
    </div>
  );
}
